import { useState, useEffect, useMemo } from "react";
import { Modal, Input, List, Typography, Avatar, Tag, Button } from "antd";
import { collection, getDocs } from "firebase/firestore";
import { db } from "@/config/firebase";
import { useImpersonationStore } from "@/store/impersonationStore";
import { useAuthStore } from "@/store/authStore";
import { useNavigate } from "react-router-dom";
import { MaterialIcon } from "./MaterialIcon";

const { Text } = Typography;

interface UserRecord {
  uid: string;
  displayName: string;
  email: string;
  plan?: { tier?: string };
  planV3?: { tier?: string };
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ImpersonateUserModal({ open, onClose }: Props) {
  const { user } = useAuthStore();
  const { startImpersonation } = useImpersonationStore();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || users.length > 0) return;

    setLoading(true);
    getDocs(collection(db, "users"))
      .then((snap) => {
        const list: UserRecord[] = snap.docs.map((doc) => {
          const data = doc.data();
          return {
            uid: doc.id,
            displayName: data.displayName || data.name || "",
            email: data.email || "",
            plan: data.plan,
            planV3: data.planV3,
          };
        });
        list.sort((a, b) => a.email.localeCompare(b.email));
        setUsers(list);
      })
      .catch((err) => console.error("Failed to load users:", err))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        u.displayName.toLowerCase().includes(q) ||
        u.uid.toLowerCase().includes(q)
    );
  }, [users, search]);

  const handleImpersonate = (u: UserRecord) => {
    if (u.uid === user?.uid) return;
    startImpersonation({ uid: u.uid, name: u.displayName || u.email, email: u.email });
    onClose();
    navigate("/");
  };

  const getTier = (u: UserRecord) => {
    return u.planV3?.tier || u.plan?.tier || "free";
  };

  return (
    <Modal
      title="Impersonate User"
      open={open}
      onCancel={onClose}
      footer={null}
      width={520}
    >
      <Input
        placeholder="Search by email, name, or UID..."
        prefix={<MaterialIcon name="search" size={18} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        style={{ marginBottom: 16 }}
      />
      <List
        loading={loading}
        dataSource={filtered}
        style={{ maxHeight: 400, overflow: "auto" }}
        renderItem={(u) => {
          const isCurrentUser = u.uid === user?.uid;
          return (
            <List.Item
              style={{ padding: "8px 0", opacity: isCurrentUser ? 0.5 : 1 }}
              actions={
                isCurrentUser
                  ? [<Text type="secondary" key="you">You</Text>]
                  : [
                      <Button
                        key="imp"
                        type="primary"
                        size="small"
                        onClick={() => handleImpersonate(u)}
                      >
                        Impersonate
                      </Button>,
                    ]
              }
            >
              <List.Item.Meta
                avatar={<Avatar icon={<MaterialIcon name="person" />} />}
                title={
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span>{u.displayName || u.email}</span>
                    <Tag style={{ fontSize: 11 }}>{getTier(u)}</Tag>
                  </span>
                }
                description={u.email}
              />
            </List.Item>
          );
        }}
      />
    </Modal>
  );
}
